import { input, output } from '@b2b/types/core';
import angular from 'angular';
import { AdvancedFiltersTriggerComponentModule } from './advanced-filters-trigger.component';
import { AdvancedFiltersService, AdvancedFiltersServiceModule } from './advanced-filters.service';
import {
  AdvancedFilterDivider,
  AdvancedFilterField,
  AdvancedFilterSection,
  AdvancedFiltersChangeEvent,
  AdvancedFiltersVM,
  RawFilters,
} from './types';

class AdvancedFiltersController {
  static $inject = ['$element', 'AdvancedFiltersService'];

  name = input.required('');
  loading = input(false);
  showOperators = input(false);
  fields = input.required<(AdvancedFilterSection | AdvancedFilterField | AdvancedFilterDivider)[]>([]);
  close = output<void>();
  clear = output<AdvancedFiltersChangeEvent>();
  submit = output<AdvancedFiltersChangeEvent>();
  init = output<void>();

  vm: AdvancedFiltersVM;

  operators = [
    { label: 'AND', value: 'and' },
    { label: 'OR', value: 'or' },
  ];

  selectedOperator: { label: string; value: string } | undefined = undefined;

  constructor(
    private $element: JQuery,
    private advancedFiltersService: AdvancedFiltersService,
  ) {}

  $onInit() {
    this.$element.addClass('acme-advanced-filters');

    if (!this.name) {
      throw new Error('acmeAdvancedFilters.name is required');
    }

    if (this.showOperators) {
      this.selectOperator(this.operators[0]);
    }

    this.vm = this.advancedFiltersService.registerComponent({ name: this.name, filtersCtrl: this });

    this.init();
  }

  $onDestroy() {
    this.advancedFiltersService.unregisterComponent({ name: this.name });
  }

  clearFilterField(field) {
    if (!field) {
      return;
    }

    if (field.type === 'section' || field.type === 'divider') {
      return;
    }

    if (field.type === 'field') {
      switch (field.format) {
        case 'Checkbox':
          this.vm.model[field.value] = false;
          break;
        case 'Select':
          this.vm.model[field.value] = null;
          break;
        case 'Dropdown':
          this.vm.model[field.value] = [];
          field.selectable.forEach((item) => {
            item.checked = false;
          });
          break;
        default:
          this.vm.model[field.value] = '';
      }
    }
  }

  clearAllFilters(options: { emitEvent?: boolean }) {
    this.fields.forEach((field) => {
      this.clearFilterField(field);
    });

    this.appliedFiltersChanged();

    const defaultOptions = { emitEvent: true };
    const normalizedOptions = Object.assign({}, defaultOptions, options || {});

    if (normalizedOptions.emitEvent) {
      const $event = this.createFiltersChangeEvent();
      this.clear({ $event });
    }
  }

  clearFilter(filterName: string) {
    const filterField = this.fields.find((field) => {
      return field.hasOwnProperty('value') && field['value'] === filterName;
    });

    this.clearFilterField(filterField);
    this.applyFilters();
  }

  updateFromRawFilters(filters: RawFilters) {
    for (const key in filters) {
      this.vm.model[key] = filters[key];
    }

    this.applyFilters();
  }

  selectOperator(operator: { label: string; value: string }) {
    this.selectedOperator = operator;
  }

  applyFilters() {
    this.appliedFiltersChanged();

    const $event = this.createFiltersChangeEvent();
    this.submit({ $event });
  }

  appliedFiltersChanged() {
    this.vm.subject.next();
  }

  /**
   * Emits a filters change event
   */
  createFiltersChangeEvent() {
    const operator = this.selectedOperator && this.selectedOperator.value;
    const rawFilters = angular.copy(this.vm.model);
    const fields = this.advancedFiltersService.mapRawFiltersToFieldsSearchParams(rawFilters);

    const $event: AdvancedFiltersChangeEvent = {
      rawFilters: rawFilters,
      fields: fields,
      operator: operator,
    };

    return $event;
  }

  disableField(field: AdvancedFilterField) {
    return this.advancedFiltersService.disableField({ componentName: this.name, field: field });
  }

  hideField(field: AdvancedFilterField) {
    return this.advancedFiltersService.hideField({ componentName: this.name, field: field });
  }
}

export const AdvancedFiltersComponentModule = angular
  .module('acme.common.component.advancedFilters', [
    'isteven-multi-select',
    AdvancedFiltersServiceModule,
    AdvancedFiltersTriggerComponentModule,
  ])
  .component('acmeAdvancedFilters', {
    templateUrl: 'common/components/advanced-filters/advanced-filters.component.tpl.html',
    controller: AdvancedFiltersController,
    bindings: {
      name: '<',
      fields: '<',
      loading: '<',
      showOperators: '<',
      close: '&',
      clear: '&',
      submit: '&',
      init: '&',
    },
  }).name;
